import React from 'react';
import { motion } from 'framer-motion';
import { PillCelebrationEffect } from '../../../components/Celebration/PillCelebrationEffect';
import { PillSadnessEffect } from '../../../components/Celebration/PillSadnessEffect';
import { CelebrationPillProps } from '../../../presentation/GamePresentationTypes';
import { arenaAudio } from '../../../services/ArenaAudioService';

const ChevronRightIcon: React.FC<{ className?: string }> = ({ className = 'text-gray-300/80' }) => (
  <motion.span
    animate={{ x: [0, 4, 0] }}
    transition={{
      duration: 1.2,
      repeat: Infinity,
      ease: 'easeInOut',
    }}
    className="inline-flex items-center justify-center shrink-0"
  >
    <svg
      className={`w-5 h-5 sm:w-6 sm:h-6 ${className} transition-all shrink-0 select-none pointer-events-none`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  </motion.span>
);

export const SpadesCelebrationPill: React.FC<CelebrationPillProps> = ({
  gameState,
  mySeat,
  onDismiss,
  bostonResult,
  isBiddingTeamSet,
  isUserTeamSet,
}) => {
  const userSeat = mySeat !== null && mySeat !== undefined ? Number(mySeat) : null;
  const userTeam = userSeat !== null ? ((userSeat % 2) + 1) : 1;

  // Determine bids and books won from authoritative state
  const rawBids = gameState?.bids || [];
  let t1Bid = 0;
  let t2Bid = 0;
  let userNil = false;
  let partnerNil = false;
  let userBooksWon = 0;

  for (const b of rawBids) {
    if (!b || b.isPass || b.bid === undefined || b.bid === null) continue;
    const s = Number(b.seat);
    const bidVal = Number(b.bid) || 0;
    if (s === 0 || s === 2) t1Bid += bidVal;
    else if (s === 1 || s === 3) t2Bid += bidVal;

    if (userSeat !== null) {
      if (s === userSeat && bidVal === 0) userNil = true;
      if (s === ((userSeat + 2) % 4) && bidVal === 0) partnerNil = true;
    }
  }

  const bts = gameState?.booksToSet;
  const finalT1Bid = bts?.team1?.bid ?? bts?.teams?.[1]?.target ?? bts?.team1?.target ?? t1Bid;
  const finalT2Bid = bts?.team2?.bid ?? bts?.teams?.[2]?.target ?? bts?.team2?.target ?? t2Bid;
  const t1Books = bts?.team1?.booksWon ?? gameState?.team1Books ?? 0;
  const t2Books = bts?.team2?.booksWon ?? gameState?.team2Books ?? 0;

  const myTeamBid = userTeam === 1 ? finalT1Bid : finalT2Bid;
  const myTeamBooks = userTeam === 1 ? t1Books : t2Books;
  const oppTeamBid = userTeam === 1 ? finalT2Bid : finalT1Bid;
  const oppTeamBooks = userTeam === 1 ? t2Books : t1Books;

  const isMyTeamMade = myTeamBooks >= myTeamBid;
  const isOppTeamMade = oppTeamBooks >= oppTeamBid;

  // Check Nil outcome if user or partner bid nil
  if (userSeat !== null && gameState.seats?.[userSeat]) {
    userBooksWon = gameState.seats[userSeat]?.booksWon ?? 0;
  }

  // Audio trigger
  const playedAudioRef = React.useRef(false);
  React.useEffect(() => {
    if (playedAudioRef.current) return;
    playedAudioRef.current = true;
    try {
      if (bostonResult) {
        if (bostonResult.isUserTeam) arenaAudio.playBostonPrizeTone();
        else arenaAudio.playDescending5ToneSet();
      } else if (userNil) {
        if (userBooksWon === 0) arenaAudio.playAscending5ToneSuccess();
        else arenaAudio.playDescending5ToneSet();
      } else if (isMyTeamMade) {
        arenaAudio.playAscending5ToneSuccess();
      } else {
        arenaAudio.playDescending5ToneSet();
      }
    } catch {}
  }, [bostonResult, userNil, userBooksWon, isMyTeamMade]);

  // Boston Clean Sweep
  if (bostonResult || t1Books === 13 || t2Books === 13) {
    const isWinner = bostonResult ? bostonResult.isUserTeam : (userTeam === 1 ? t1Books === 13 : t2Books === 13);
    return (
      <div className="absolute bottom-8 sm:bottom-11 md:bottom-14 left-1/2 -translate-x-1/2 z-50 pointer-events-auto select-none max-w-[90vw]">
        {isWinner ? (
          <div className="relative">
            <PillCelebrationEffect />
            <motion.button
              type="button"
              onClick={onDismiss}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.96 }}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1, y: [0, -6, 0] }}
              transition={{
                y: { repeat: Infinity, duration: 1.1, ease: 'easeInOut' },
                opacity: { duration: 0.25 },
                scale: { duration: 0.25 },
              }}
              aria-label="Next"
              className="relative z-20 group flex items-center justify-between gap-3 sm:gap-5 px-5 sm:px-6 py-2.5 sm:py-3.5 rounded-full bg-gradient-to-r from-purple-500 via-indigo-600 to-purple-700 hover:from-purple-400 hover:via-indigo-500 hover:to-purple-600 shadow-[0_10px_30px_rgba(168,85,247,0.45),0_2px_10px_rgba(0,0,0,0.4)] backdrop-blur-xl text-white whitespace-nowrap cursor-pointer transition-all active:scale-95"
            >
              <div className="flex items-center gap-2 sm:gap-2.5">
                <span className="text-sm sm:text-base leading-none">🏆</span>
                <span className="text-xs sm:text-sm font-black uppercase tracking-wider text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
                  Boston Clean Sweep! (13 Tricks)
                </span>
              </div>
              <ChevronRightIcon className="text-purple-100/90 group-hover:text-white group-hover:translate-x-0.5" />
            </motion.button>
          </div>
        ) : (
          <div className="relative">
            <PillSadnessEffect variant="set" />
            <motion.button
              type="button"
              onClick={onDismiss}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.96 }}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1, y: [0, -6, 0] }}
              transition={{
                y: { repeat: Infinity, duration: 1.1, ease: 'easeInOut' },
                opacity: { duration: 0.25 },
                scale: { duration: 0.25 },
              }}
              aria-label="Next"
              className="relative z-20 group flex items-center justify-between gap-3 sm:gap-5 px-5 sm:px-6 py-2.5 sm:py-3.5 rounded-full bg-gradient-to-r from-red-500 via-rose-600 to-red-700 hover:from-red-400 hover:via-rose-500 hover:to-red-600 shadow-[0_10px_30px_rgba(244,63,94,0.45),0_2px_10px_rgba(0,0,0,0.4)] backdrop-blur-xl text-white whitespace-nowrap cursor-pointer transition-all active:scale-95"
            >
              <div className="flex items-center gap-2 sm:gap-2.5">
                <span className="text-sm sm:text-base leading-none">💥</span>
                <span className="text-xs sm:text-sm font-black uppercase tracking-wider text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
                  Opponents Swept All 13 Tricks!
                </span>
              </div>
              <ChevronRightIcon className="text-rose-100/90 group-hover:text-white group-hover:translate-x-0.5" />
            </motion.button>
          </div>
        )}
      </div>
    );
  }

  // Nil Bid Outcomes
  if (userNil) {
    const nilMade = userBooksWon === 0;
    return (
      <div className="absolute bottom-8 sm:bottom-11 md:bottom-14 left-1/2 -translate-x-1/2 z-50 pointer-events-auto select-none max-w-[90vw]">
        {nilMade ? (
          <div className="relative">
            <PillCelebrationEffect />
            <motion.button
              type="button"
              onClick={onDismiss}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.96 }}
              initial={{ opacity: 0, scale: 0.75 }}
              animate={{ opacity: 1, scale: 1, y: [0, -6, 0] }}
              transition={{
                y: { repeat: Infinity, duration: 1.1, ease: 'easeInOut' },
                opacity: { duration: 0.25 },
                scale: { duration: 0.25 },
              }}
              aria-label="Next"
              className="relative z-20 group flex items-center justify-between gap-3 sm:gap-5 px-5 sm:px-6 py-2.5 sm:py-3.5 rounded-full bg-gradient-to-r from-emerald-500 via-emerald-600 to-teal-700 hover:from-emerald-400 hover:via-emerald-500 hover:to-teal-600 shadow-[0_10px_30px_rgba(16,185,129,0.45),0_2px_10px_rgba(0,0,0,0.4)] backdrop-blur-xl text-white whitespace-nowrap cursor-pointer transition-all active:scale-95"
            >
              <div className="flex items-center gap-2 sm:gap-2.5">
                <span className="text-sm sm:text-base leading-none">🛡️</span>
                <span className="text-xs sm:text-sm font-black uppercase tracking-wider text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
                  Nil Made! (+100 Bonus)
                </span>
              </div>
              <ChevronRightIcon className="text-emerald-100/90 group-hover:text-white group-hover:translate-x-0.5" />
            </motion.button>
          </div>
        ) : (
          <div className="relative">
            <PillSadnessEffect variant="set" />
            <motion.button
              type="button"
              onClick={onDismiss}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.96 }}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1, y: [0, -6, 0] }}
              transition={{
                y: { repeat: Infinity, duration: 1.1, ease: 'easeInOut' },
                opacity: { duration: 0.25 },
                scale: { duration: 0.25 },
              }}
              aria-label="Next"
              className="relative z-20 group flex items-center justify-between gap-3 sm:gap-5 px-5 sm:px-6 py-2.5 sm:py-3.5 rounded-full bg-gradient-to-r from-red-500 via-rose-600 to-red-700 hover:from-red-400 hover:via-rose-500 hover:to-red-600 shadow-[0_10px_30px_rgba(244,63,94,0.45),0_2px_10px_rgba(0,0,0,0.4)] backdrop-blur-xl text-white whitespace-nowrap cursor-pointer transition-all active:scale-95"
            >
              <div className="flex items-center gap-2 sm:gap-2.5">
                <span className="text-sm sm:text-base leading-none">💥</span>
                <span className="text-xs sm:text-sm font-black uppercase tracking-wider text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
                  Nil Set! (-100 Penalty)
                </span>
              </div>
              <ChevronRightIcon className="text-rose-100/90 group-hover:text-white group-hover:translate-x-0.5" />
            </motion.button>
          </div>
        )}
      </div>
    );
  }

  // Standard Team Partnership Outcomes
  return (
    <div className="absolute bottom-8 sm:bottom-11 md:bottom-14 left-1/2 -translate-x-1/2 z-50 pointer-events-auto select-none max-w-[90vw]">
      {isMyTeamMade ? (
        <div className="relative">
          <PillCelebrationEffect />
          <motion.button
            type="button"
            onClick={onDismiss}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.96 }}
            initial={{ opacity: 0, scale: 0.75 }}
            animate={{ opacity: 1, scale: 1, y: [0, -6, 0] }}
            transition={{
              y: { repeat: Infinity, duration: 1.1, ease: 'easeInOut' },
              opacity: { duration: 0.25 },
              scale: { duration: 0.25 },
            }}
            aria-label="Next"
            className="relative z-20 group flex items-center justify-between gap-4 sm:gap-6 px-6 sm:px-8 py-3.5 sm:py-4.5 rounded-full bg-gradient-to-r from-purple-500 via-indigo-600 to-purple-700 hover:from-purple-400 hover:via-indigo-500 hover:to-purple-600 shadow-[0_10px_30px_rgba(168,85,247,0.45),0_2px_10px_rgba(0,0,0,0.4)] backdrop-blur-xl text-white whitespace-nowrap cursor-pointer transition-all active:scale-95"
          >
            <div className="flex items-center gap-2.5 sm:gap-3">
              <span className="text-base sm:text-xl leading-none">♠</span>
              <span className="text-sm sm:text-base font-black uppercase tracking-wider text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
                Contract Made! ({myTeamBooks}/{myTeamBid} Tricks)
              </span>
            </div>
            <ChevronRightIcon className="text-purple-100/90 group-hover:text-white group-hover:translate-x-0.5" />
          </motion.button>
        </div>
      ) : (
        <div className="relative">
          <PillSadnessEffect variant="set" />
          <motion.button
            type="button"
            onClick={onDismiss}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.96 }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1, y: [0, -6, 0] }}
            transition={{
              y: { repeat: Infinity, duration: 1.1, ease: 'easeInOut' },
              opacity: { duration: 0.25 },
              scale: { duration: 0.25 },
            }}
            aria-label="Next"
            className="relative z-20 group flex items-center justify-between gap-4 sm:gap-6 px-6 sm:px-8 py-3.5 sm:py-4.5 rounded-full bg-gradient-to-r from-red-500 via-rose-600 to-red-700 hover:from-red-400 hover:via-rose-500 hover:to-red-600 shadow-[0_10px_30px_rgba(244,63,94,0.45),0_2px_10px_rgba(0,0,0,0.4)] backdrop-blur-xl text-white whitespace-nowrap cursor-pointer transition-all active:scale-95"
          >
            <div className="flex items-center gap-2.5 sm:gap-3">
              <span className="text-base sm:text-xl leading-none">💥</span>
              <span className="text-sm sm:text-base font-black uppercase tracking-wider text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
                Team Got Set! ({myTeamBooks}/{myTeamBid} Tricks)
              </span>
            </div>
            <ChevronRightIcon className="text-rose-100/90 group-hover:text-white group-hover:translate-x-0.5" />
          </motion.button>
        </div>
      )}
    </div>
  );
};
